<script setup lang="ts">
/**
 * Satisfies App\Http\Middleware\ReauthenticateUser. This was a password field inline in App.vue; a guest who signs in
 * with a passkey may not remember a password at all, so any two-factor method they have enrolled is offered as well.
 *
 * @changelog
 *
 * 2026-09-09 (0.1.0) (jhoward)
 *   - Add App\components\ReauthenticateDialog.vue, replacing the password-only dialog in App.vue.
 *
 * 2026-09-18 (0.2.0) (jhoward)
 *   - Replace the hand-written codePattern with pattern="otp-6", which states the same six digits and adds the
 *     autocomplete="one-time-code" this field never carried.
 *
 * 2026-10-10 (0.3.0) (jhoward)
 *   - Lay the method choices out in an equal-width auto-fit grid, and zero the big links' margin so margin plus
 *     width: 100% no longer clips them when the grid collapses to one column.
 *   - Lead with the passkey: when the guest has one and the browser can use it, the dialog opens straight on the
 *     passkey prompt, with "Use a different method" for the password and codes. A tap is the whole reauthentication.
 *   - A cancelled or failed prompt keeps the passkey panel and says why, in the guest's terms (webAuthnSupport.ts),
 *     rather than bouncing back to the chooser with the browser's own message; Confirm runs it again with the user
 *     gesture Safari wants. The passkey is not offered on a browser without WebAuthn.
 *   - Call it a passkey.
 *
 * 2026-10-10 (0.4.0) (jhoward)
 *   - Offer an authenticator-app code: a code field with nothing to send.
 *
 * 2026-10-23 (0.5.0) (jhoward)
 *   - Offer the passkey only where canUsePasskeysHere() says this browser can use one on this domain.
 *
 * 2026-10-27 (0.6.0) (jhoward)
 *   - A text refused because the number stopped texts from us (SMS_OPTED_OUT) shows SmsOptedOutNotice in place of the
 *     code field: text START to our number, then try again.
 */

import MaterialFormText from '@framework/components/material/MaterialFormText.vue';
import PeppermillBigLink from '@framework/components/PeppermillBigLink.vue';
import PeppermillDialog from '@framework/components/PeppermillDialog.vue';
import type { FetchResponse } from '@framework/defs/framework';
import { recursiveBase64StrToArrayBuffer, arrayBufferToBase64 } from '@framework/helpers';
import { computed, ref, watch } from 'vue';
import SmsOptedOutNotice from '@/components/SmsOptedOutNotice.vue';
import peppermillCustomFetchProxy from '@/helpers/peppermillCustomFetchProxy';
import type { SmsOptIn } from '@/helpers/smsOptedOut';
import smsOptedOut from '@/helpers/smsOptedOut';
import { canUsePasskeysHere, describeWebAuthnError } from '@/helpers/webAuthnSupport';
import useAppStore from '@/store';
import { reauthenticate } from '@rss/routes';
import { challenge as webAuthnChallenge, verify as webAuthnVerify } from '@rss/routes/login/two-factor/webauthn';
import { methods as reauthenticateMethods, requestCode, verifyCode } from '@rss/routes/reauthenticate';

export type ReauthenticateMethod = 'password' | 'email' | 'sms' | 'webauthn' | 'authenticator';

interface MethodsResponse extends FetchResponse {
  data?: {
    methods_available: ReauthenticateMethod[];
  };
}

interface RequestCodeResponse extends FetchResponse {
  data?: {
    retry_after_seconds?: number;
    recipient?: string;
  };
}

const emit = defineEmits<{
  (event: 'success'): void;
  (event: 'logout'): void;
}>();

const appStore = useAppStore();

const methodsAvailable = ref<ReauthenticateMethod[]>(['password']);
const selectedMethod = ref<ReauthenticateMethod | null>(null);
const password = ref<string>('');
const code = ref<string>('');
const recipient = ref<string>('');
const isCodeSent = ref<boolean>(false);
/** Why the last passkey attempt did not complete, shown on the passkey panel; empty while the prompt is open. */
const passkeyError = ref<string>('');
/** Set when a text was refused because the number stopped texts from us; how to turn them back on. */
const smsOptIn = ref<SmsOptIn | null>(null);

const methodLabels: { [key in ReauthenticateMethod]: string } = {
  password: 'Use my password',
  email: 'Email me a code',
  sms: 'Text me a code',
  webauthn: 'Use a passkey',
  authenticator: 'Use my authenticator app',
};

const methodIcons: { [key in ReauthenticateMethod]: string } = {
  password: 'password',
  email: 'email',
  sms: 'sms',
  webauthn: 'key',
  authenticator: 'phonelink_lock',
};

//Passkey first, then the password, then the codes: the order a guest who has a passkey would want.
const methodOrder: ReauthenticateMethod[] = ['webauthn', 'password', 'authenticator', 'email', 'sms'];

watch(
  () => appStore.state.reauthenticationRequired,
  async (isRequired) => {
    if (!isRequired) {
      return;
    }

    resetState();

    const response = await peppermillCustomFetchProxy<MethodsResponse>(
      {},
      {
        route: reauthenticateMethods(),
        showLoadingSpinner: false,
      },
    );

    const offered = response.data?.methods_available?.length ? response.data.methods_available : ['password'];
    const passkeysHere = await canUsePasskeysHere();
    methodsAvailable.value = methodOrder.filter(
      (method) => offered.includes(method) && ('webauthn' !== method || passkeysHere),
    );

    //A passkey is one tap, so start there; otherwise with one method there is nothing to choose.
    if (methodsAvailable.value.includes('webauthn')) {
      await selectMethod('webauthn');
    } else if (methodsAvailable.value.length === 1) {
      await selectMethod(methodsAvailable.value[0]);
    }
  },
  { immediate: true },
);

function resetState(): void {
  selectedMethod.value = null;
  password.value = '';
  code.value = '';
  recipient.value = '';
  isCodeSent.value = false;
  passkeyError.value = '';
  smsOptIn.value = null;
}

async function selectMethod(method: ReauthenticateMethod): Promise<void> {
  selectedMethod.value = method;
  code.value = '';
  isCodeSent.value = false;
  passkeyError.value = '';

  if ('email' === method || 'sms' === method) {
    await sendCode(method);
  }

  if ('webauthn' === method) {
    await verifyWithPasskey();
  }
}

async function sendCode(method: 'email' | 'sms'): Promise<void> {
  const response = await peppermillCustomFetchProxy<RequestCodeResponse>(
    {},
    {
      route: requestCode({ method }),
    },
  );

  smsOptIn.value = smsOptedOut(response);
  if (smsOptIn.value) {
    isCodeSent.value = false;
    return;
  }

  //429 means a code is already in flight and still valid, so the guest can still type that one in.
  if (200 === response.status || 429 === response.status) {
    isCodeSent.value = true;
    recipient.value = response.data?.recipient || '';
  }

  if (response.message) {
    appStore.setNewSnack(15000, response.message, 200 === response.status ? 'check' : 'error');
  }
}

async function verifyWithPasskey(): Promise<void> {
  passkeyError.value = '';

  const response = await peppermillCustomFetchProxy<FetchResponse & { data: CredentialRequestOptions }>(
    {},
    {
      route: webAuthnChallenge({ requestType: 'reauthentication' }),
    },
  );

  if (200 !== response.status || !response.data) {
    passkeyError.value = response.message || 'Could not start the passkey prompt.';
    return;
  }

  const challenge = response.data;
  recursiveBase64StrToArrayBuffer(challenge);

  try {
    const credential = (await navigator.credentials.get(challenge)) as PublicKeyCredential | null;
    if (!credential) {
      passkeyError.value = 'No passkey was used. Press Confirm to try again.';
      return;
    }

    const assertion = credential.response as AuthenticatorAssertionResponse;
    const verifyResponse = await peppermillCustomFetchProxy(
      {
        id: credential.rawId ? arrayBufferToBase64(credential.rawId) : null,
        clientDataJSON: assertion.clientDataJSON ? arrayBufferToBase64(assertion.clientDataJSON) : null,
        authenticatorData: assertion.authenticatorData ? arrayBufferToBase64(assertion.authenticatorData) : null,
        signature: assertion.signature ? arrayBufferToBase64(assertion.signature) : null,
        userHandle: assertion.userHandle ? arrayBufferToBase64(assertion.userHandle) : null,
      },
      {
        route: webAuthnVerify({ requestType: 'reauthentication' }),
      },
    );

    if (200 !== verifyResponse.status) {
      passkeyError.value = verifyResponse.message || 'Passkey verification failed. Press Confirm to try again.';
      return;
    }

    handleResult(verifyResponse.status, verifyResponse.message);
  } catch (err) {
    //Stay on the passkey panel. Confirm re-runs the prompt with a user gesture, which some browsers need.
    passkeyError.value = describeWebAuthnError(err, 'get');
  }
}

/**
 * The dialog's confirm button. What it submits depends on the method in play; for a passkey there is nothing to
 * submit, since that flow completes in the authenticator prompt - Confirm opens it again.
 */
async function confirm(): Promise<void> {
  if ('password' === selectedMethod.value) {
    const response = await peppermillCustomFetchProxy({ password: password.value }, { route: reauthenticate() });
    handleResult(response.status, response.message);
    return;
  }

  if ('email' === selectedMethod.value || 'sms' === selectedMethod.value || 'authenticator' === selectedMethod.value) {
    const response = await peppermillCustomFetchProxy(
      { code: code.value },
      {
        route: verifyCode({ method: selectedMethod.value }),
      },
    );
    handleResult(response.status, response.message);
    return;
  }

  if ('webauthn' === selectedMethod.value) {
    await verifyWithPasskey();
  }
}

function handleResult(status: number, message?: string): void {
  if (200 === status) {
    resetState();
    emit('success');
  }

  appStore.setNewSnack(
    15000,
    message || 'Re-authentication failed. Please try again.',
    200 === status ? 'check' : 'error',
  );
}

function deny(): void {
  resetState();
  emit('logout');
}

const isChoosing = computed<boolean>(() => null === selectedMethod.value);
</script>

<template>
  <peppermill-dialog
    title="Re-authentication Required"
    is-active
    :callback-confirm="confirm"
    :callback-deny="deny"
    label-confirm="Confirm"
    label-deny="Logout"
  >
    <p>Please confirm it is you, so we can make sure only authorized users can access sensitive information.</p>

    <div v-if="isChoosing" class="reauthenticate-methods">
      <peppermill-big-link
        v-for="method in methodsAvailable"
        :key="method"
        path="#"
        :icon="methodIcons[method]"
        :title="methodLabels[method]"
        @click.prevent="selectMethod(method)"
      ></peppermill-big-link>
    </div>

    <template v-else>
      <material-form-text
        v-if="'password' === selectedMethod"
        label="Password"
        name="password"
        type="password"
        required
        :value="password"
        @update:value="password = $event"
      ></material-form-text>

      <sms-opted-out-notice
        v-if="'sms' === selectedMethod && smsOptIn"
        :opt-in="smsOptIn"
        retry-label="Try again"
        @retry="sendCode('sms')"
      ></sms-opted-out-notice>
      <template v-else-if="'email' === selectedMethod || 'sms' === selectedMethod">
        <p v-if="isCodeSent && recipient">We sent a code to {{ recipient }}.</p>
        <material-form-text
          label="Verification Code"
          name="code"
          pattern="otp-6"
          :value="code"
          @update:value="code = $event"
        ></material-form-text>
        <p><a href="#" @click.prevent="sendCode(selectedMethod as 'email' | 'sms')">Send a new code</a></p>
      </template>

      <template v-if="'authenticator' === selectedMethod">
        <p>Enter the 6-digit code from your authenticator app.</p>
        <material-form-text
          label="Code from the app"
          name="code"
          pattern="otp-6"
          :value="code"
          @update:value="code = $event"
        ></material-form-text>
      </template>

      <template v-if="'webauthn' === selectedMethod">
        <p v-if="passkeyError">{{ passkeyError }}</p>
        <p v-else>Use your passkey to confirm it's you. Follow the prompt from your device.</p>
        <p>If nothing happened, press Confirm to try again.</p>
      </template>

      <p v-if="methodsAvailable.length > 1">
        <a href="#" @click.prevent="resetState">Use a different method</a>
      </p>
    </template>
  </peppermill-dialog>
</template>

<style scoped>
/*As many equal-width columns as fit; a lone column (narrow dialog) just becomes one full-width column.*/
.reauthenticate-methods {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 140px), 1fr));
  gap: 8px;
  margin: 0 8px;
}

/*The big link carries margin: 4px on top of width: 100%, which overflows (and clips) its grid cell; the grid gap
  does the spacing instead.*/
.reauthenticate-methods > :deep(.big_link--container) {
  display: block;
  width: 100%;
  margin: 0;
}
</style>
